import { useEffect, useMemo, useRef, useState } from "react";
import { agentApi } from "../../../api/client";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";
import KillSwitchIndicator from "./KillSwitchIndicator";

// Phase B per agent-detail-viewer-v1.md §10 row 11. Filter bar +
// metrics summary + 3 row panels (conversations, verifications,
// query_traces) + kill-switch indicator. Polls /activity every 5s
// with current filters; click any row → drawer.

const RANGES = ["24h", "7d", "30d"];
const VERDICTS = ["ACCURATE", "PARTIALLY_ACCURATE", "INACCURATE", "UNKNOWN", "SKIPPED"];
const VERDICT_COLOR = {
  ACCURATE: COLORS.green,
  PARTIALLY_ACCURATE: COLORS.orange,
  INACCURATE: COLORS.pink,
  UNKNOWN: COLORS.textDim,
  SKIPPED: COLORS.textDim,
};
const STATUS_COLOR = {
  resolved: COLORS.green, escalated: COLORS.pink, started: COLORS.cyan,
  active: COLORS.cyan, abandoned: COLORS.textDim, handed_off: COLORS.orange,
};

const Card = ({ title, count, children }) => (
  <div style={{
    padding: 16, borderRadius: 12,
    border: `1px solid ${COLORS.border}`, background: COLORS.surface,
    boxShadow: MATERIALS.spatialShadow(),
  }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</div>
      {count != null && <span style={{ fontSize: 11, color: COLORS.textDim }}>{count} {count === 1 ? "row" : "rows"}</span>}
    </div>
    {children}
  </div>
);

const Stat = ({ label, value, color }) => (
  <div style={{
    padding: 10, borderRadius: 8,
    border: `1px solid ${COLORS.border}`, background: COLORS.surface,
    minWidth: 110, display: "flex", flexDirection: "column", gap: 2,
  }}>
    <div style={{ fontSize: 9, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
    <div style={{ fontSize: 17, fontWeight: 700, color: color || COLORS.text }}>{value}</div>
  </div>
);

// Exported for unit-testing: ISO → locale string, em-dash for empty.
export const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";
// Exported for unit-testing: length-capped string with ellipsis.
export const truncate = (s, n = 80) => (s == null ? "" : (s.length > n ? s.slice(0, n) + "…" : s));

// Exported for unit-testing: assemble the /activity query params from the
// filter-bar state. Only non-empty axes are included so the request stays
// byte-identical to "no filter" when the user hasn't narrowed anything.
export function buildActivityQueryParams(range, verdictSet, expertId) {
  const p = { range };
  if (verdictSet && verdictSet.size) p.verdict = Array.from(verdictSet).join(",");
  if (expertId) p.expert_id = expertId;
  return p;
}

export default function ActivityTab({ tree, currentUser, onOpenDrawer }) {
  const supervisor = tree?.agent;
  const experts = tree?.experts || [];
  const isSupervisor = (tree?.agent_kind || "regular") === "supervisor";
  const canMutate = currentUser?.role === "owner" || currentUser?.role === "administrator";

  const [range, setRange] = useState("24h");
  const [verdictFilter, setVerdictFilter] = useState(new Set());  // Set<verdict>
  const [expertId, setExpertId] = useState("");                    // "" = all
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const queryParams = useMemo(
    () => buildActivityQueryParams(range, verdictFilter, expertId),
    [range, verdictFilter, expertId],
  );

  const tickRef = useRef(0);
  useEffect(() => {
    if (!supervisor?.id) return;
    let cancelled = false;
    const tick = ++tickRef.current;
    const fetchOnce = (initial) => {
      if (initial) setLoading(true);
      agentApi.getActivity(supervisor.id, queryParams)
        .then(res => {
          if (cancelled || tick !== tickRef.current) return;
          setData(res?.data || null);
          setErr(null);
        })
        .catch(e => { if (!cancelled) setErr(e?.message || String(e)); })
        .finally(() => { if (!cancelled && initial) setLoading(false); });
    };
    fetchOnce(true);
    const t = setInterval(() => fetchOnce(false), 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [supervisor?.id, queryParams]);

  const m = data?.metrics || {};
  const conversations = data?.conversations?.rows || [];
  const verifications = data?.verifications?.rows || [];
  const qts = data?.query_traces?.rows || [];

  const toggleVerdict = (v) => {
    setVerdictFilter(prev => {
      const n = new Set(prev);
      n.has(v) ? n.delete(v) : n.add(v);
      return n;
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Filter bar */}
      <div style={{
        padding: "10px 14px", borderRadius: 10,
        border: `1px solid ${COLORS.border}`, background: COLORS.surface,
        display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: COLORS.textDim }}>range</span>
          {RANGES.map(r => (
            <button key={r} onClick={() => setRange(r)} style={{
              padding: "4px 10px", borderRadius: 6, fontSize: 11,
              border: `1px solid ${range === r ? COLORS.accent : COLORS.border}`,
              background: range === r ? COLORS.accentSoft : "transparent",
              color: range === r ? COLORS.text : COLORS.textMuted, cursor: "pointer",
            }}>{r}</button>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: COLORS.textDim }}>verdict</span>
          {VERDICTS.map(v => {
            const on = verdictFilter.has(v);
            const c = VERDICT_COLOR[v];
            return (
              <button key={v} onClick={() => toggleVerdict(v)} style={{
                padding: "3px 9px", borderRadius: 5, fontSize: 10, fontWeight: 600,
                border: `1px solid ${on ? c : COLORS.border}`,
                background: on ? `${c}22` : "transparent",
                color: on ? c : COLORS.textMuted, cursor: "pointer",
              }}>{v}</button>
            );
          })}
          {verdictFilter.size > 0 && (
            <button onClick={() => setVerdictFilter(new Set())} style={{
              fontSize: 10, color: COLORS.textDim, background: "transparent",
              border: "none", cursor: "pointer", marginLeft: 4,
            }}>clear</button>
          )}
        </div>

        {isSupervisor && experts.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, color: COLORS.textDim }}>expert</span>
            <select value={expertId} onChange={e => setExpertId(e.target.value)}
              style={{
                fontSize: 11, padding: "4px 8px", borderRadius: 6,
                border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text,
              }}
            >
              <option value="">all (supervisor + experts)</option>
              <option value={supervisor.id}>{supervisor.name} (supervisor only)</option>
              {experts.map(e => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
        )}

        <span style={{ marginLeft: "auto", fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>
          {loading ? "loading…" : "polling every 5s"}
        </span>
      </div>

      <KillSwitchIndicator canMutate={canMutate} />

      {err && <div style={{ color: COLORS.pink, fontSize: 12 }}>Failed: {err}</div>}

      {/* Metrics summary panel */}
      <Card title={`Headline metrics (${range})`}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Stat label="Conversations" value={m.conversation_count ?? "—"} />
          <Stat label="Avg CSAT" value={m.avg_csat != null ? m.avg_csat.toFixed(2) : "—"} />
          <Stat label="Escalation"
                value={m.conversation_count ? `${((m.escalation_rate || 0) * 100).toFixed(0)}%` : "—"}
                color={m.escalation_rate > 0.2 ? COLORS.pink : null} />
          <Stat label="Query traces" value={m.query_trace_count ?? "—"} />
          <Stat label="Avg latency"
                value={m.avg_query_latency_ms != null ? `${Math.round(m.avg_query_latency_ms)}ms` : "—"} />
          <Stat label="Empty rate"
                value={m.query_trace_count ? `${((m.empty_query_rate || 0) * 100).toFixed(0)}%` : "—"}
                color={m.empty_query_rate > 0.05 ? COLORS.orange : null} />
        </div>
        {m.verdict_breakdown && Object.keys(m.verdict_breakdown).length > 0 && (
          <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
            {VERDICTS.map(v => {
              const n = m.verdict_breakdown[v];
              if (!n) return null;
              const c = VERDICT_COLOR[v];
              return (
                <span key={v} style={{
                  padding: "3px 9px", borderRadius: 5, fontSize: 10,
                  background: `${c}22`, color: c, border: `1px solid ${c}55`,
                }}>{v}: <strong>{n}</strong></span>
              );
            })}
          </div>
        )}
      </Card>

      {/* Conversations panel */}
      <Card title="Conversations" count={conversations.length}>
        {conversations.length === 0 ? (
          <div style={{ color: COLORS.textDim, fontSize: 12 }}>No conversations in window.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {conversations.map(c => (
              <div key={c.id}
                onClick={() => onOpenDrawer && onOpenDrawer({
                  kind: `Conversation: ${c.id.slice(0, 8)}`,
                  payload: c,
                })}
                style={{
                  padding: "8px 12px", borderRadius: 8,
                  border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                  cursor: "pointer", fontSize: 12,
                  display: "grid", gridTemplateColumns: "120px 80px 100px 1fr 60px 110px",
                  gap: 8, alignItems: "center",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.accent; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; }}
              >
                <span style={{ color: COLORS.textDim, fontFamily: "monospace", fontSize: 10 }}>{c.id.slice(0, 8)}…</span>
                <span style={{ fontSize: 10 }}>{c.channel}</span>
                <span style={{ fontSize: 10, color: STATUS_COLOR[c.status] || COLORS.textDim }}>{c.status}</span>
                <span style={{ color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.summary || c.intent || "(no summary)"}
                </span>
                <span style={{ fontSize: 10, color: c.escalated ? COLORS.pink : COLORS.textDim }}>
                  {c.escalated ? "escalated" : ""}
                  {c.csat_score ? ` ★${c.csat_score}` : ""}
                </span>
                <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>{fmt(c.started_at)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Verifications panel */}
      <Card title="Fact-checker verdicts" count={verifications.length}>
        {verifications.length === 0 ? (
          <div style={{ color: COLORS.textDim, fontSize: 12 }}>No verifications in window.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {verifications.map(v => {
              const c = VERDICT_COLOR[v.verdict] || COLORS.textDim;
              return (
                <div key={v.id}
                  onClick={() => onOpenDrawer && onOpenDrawer({
                    kind: `Verification: ${v.verdict}`,
                    payload: v,
                  })}
                  style={{
                    padding: "8px 12px", borderRadius: 8,
                    border: `1px solid ${c}33`, background: COLORS.surface,
                    cursor: "pointer", fontSize: 12,
                    display: "grid", gridTemplateColumns: "150px 80px 1fr 70px 110px",
                    gap: 8, alignItems: "center",
                  }}
                >
                  <span style={{ color: c, fontWeight: 600, fontSize: 10 }}>{v.verdict}</span>
                  <span style={{ fontSize: 10, color: COLORS.textDim }}>
                    {v.confidence_score != null ? v.confidence_score.toFixed(2) : "—"}
                  </span>
                  <span style={{ color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {truncate(v.llm_response_preview, 100)}
                  </span>
                  <span style={{ fontSize: 10, color: COLORS.textDim }}>
                    {v.verification_path}
                    {v.re_prompt_count ? ` ⟲${v.re_prompt_count}` : ""}
                  </span>
                  <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>{fmt(v.created_at)}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Query traces panel */}
      <Card title="Query traces" count={qts.length}>
        {qts.length === 0 ? (
          <div style={{ color: COLORS.textDim, fontSize: 12 }}>No traces in window.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {qts.map(q => (
              <div key={q.id}
                onClick={() => onOpenDrawer && onOpenDrawer({
                  kind: `Query trace: ${q.id.slice(0, 8)}`,
                  payload: q,
                })}
                style={{
                  padding: "8px 12px", borderRadius: 8,
                  border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                  cursor: "pointer", fontSize: 12,
                  display: "grid", gridTemplateColumns: "1fr 70px 70px 80px 110px",
                  gap: 8, alignItems: "center",
                }}
              >
                <span style={{ color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {truncate(q.question_preview, 100)}
                </span>
                <span style={{ fontSize: 10, color: q.total_latency_ms > 5000 ? COLORS.orange : COLORS.textDim }}>
                  {q.total_latency_ms != null ? `${q.total_latency_ms}ms` : "—"}
                </span>
                <span style={{ fontSize: 10, color: COLORS.textDim }}>
                  {q.total_llm_calls ?? "—"} llm{q.total_llm_calls === 1 ? "" : "s"}
                </span>
                <span style={{ fontSize: 10, color: q.result_empty ? COLORS.orange : COLORS.green }}>
                  {q.cache_hit ? "cache" : (q.result_empty ? "empty" : "ok")}
                  {q.synthesis_conflicts ? ` · ${q.synthesis_conflicts}c` : ""}
                </span>
                <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>{fmt(q.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
